import { truckServiceItemListQuerySchema, type Page, type TruckServiceItemListQuery, type TruckServiceOverrideInput } from '@chukua/common';
import { useCallback, useEffect, useState } from 'react';
import { useListQuery } from '../../../../../shared/hooks/use-list-query';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { toListParams } from '../../../../../shared/policies/list-params';
import { ApiError } from '../../../../../shared/shared.api';
import {
  listTruckServiceItems,
  listTruckServiceOverrides,
  removeTruckServiceOverride,
  setTruckServiceOverride,
  type TruckServiceItemView,
  type TruckServiceOverrideView,
} from '../truck-service.api';

export type TruckServicePlanState =
  | { status: 'loading' }
  | { status: 'loaded'; page: Page<TruckServiceItemView>; excluded: TruckServiceOverrideView[]; queryKey: string }
  | { status: 'forbidden' }
  | { status: 'error' };

const keyOf = (query: TruckServiceItemListQuery) => toListParams(query).toString();

async function loadPlan(truckId: string, query: TruckServiceItemListQuery): Promise<TruckServicePlanState> {
  try {
    const [page, overrides] = await Promise.all([listTruckServiceItems(truckId, query), listTruckServiceOverrides(truckId)]);
    return { status: 'loaded', page, excluded: overrides.filter((o) => o.isExcluded), queryKey: keyOf(query) };
  } catch (err) {
    return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
  }
}

function toSaveError(err: unknown): string {
  return apiErrorMessage(err) ?? 'Could not save — check the fields and try again.';
}

/**
 * A truck's resolved service plan (the class's plan with this truck's overrides applied, read live from the server) and its
 * exclusions, which the plan itself leaves out. A form failure stays inline; removing or restoring an override toasts.
 */
export function useTruckServicePlan(truckId: string) {
  const list = useListQuery(truckServiceItemListQuerySchema);
  const { query } = list;
  const [state, setState] = useState<TruckServicePlanState>({ status: 'loading' });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const fetchPlan = useCallback(() => loadPlan(truckId, query), [truckId, query]);

  useEffect(() => {
    let cancelled = false;
    fetchPlan().then((next) => {
      if (!cancelled) setState(next);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchPlan]);

  async function save(action: () => Promise<unknown>, successMessage: string): Promise<boolean> {
    setSaveError(null);
    setSaving(true);
    try {
      await action();
      setState(await fetchPlan());
      toast.success(successMessage);
      return true;
    } catch (err) {
      setSaveError(toSaveError(err));
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function rowAction(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<void> {
    try {
      await action();
      setState(await fetchPlan());
      toast.success(successMessage);
    } catch {
      toast.error(errorMessage);
    }
  }

  return {
    state,
    list,
    isRefreshing: state.status === 'loaded' && state.queryKey !== keyOf(query),
    saveError,
    saving,
    resetSaveError: () => setSaveError(null),
    setOverride: (item: { id: string; name: string }, input: TruckServiceOverrideInput) =>
      save(
        () => setTruckServiceOverride(truckId, item.id, input),
        input.isExcluded ? `${item.name} left out for this truck` : `${item.name} changed for this truck`,
      ),
    removeOverride: (item: { id: string; name: string }) =>
      rowAction(() => removeTruckServiceOverride(truckId, item.id), `${item.name} follows the class again`, `Couldn't restore ${item.name}`),
  };
}
